import React from "react";
import {GameLoadingIndicatorBaseModel} from "./game-loading-indicator-base.model";
import {
    HybridEmbeddedGameLoadingIndicatorComponent
} from "../../../components/game-window/loading-indicators/hybrid-embedded/hybrid-embedded-game-loading-indicator.component";
import {
    IHybridEmbeddedGameLoadingIndicatorViewModel
} from "./hybrid-embedded-game-loading-indicator-view-model.interface";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {CGPEventSubscription} from "../../sdk/cgp-sdk.interface";
import { observable, runInAction, makeObservable } from "mobx";
import {GameRunnerService} from "../game-runner.service";
import {TOAST_TYPES} from "../../../../sdk/src/services/toaster/toaster_service_consts";
import {ON_DEMAND} from "../../../../sdk/src/services/hybrid/native/native_const";
import {Check, TimeSpan} from "@sparkware/app-utils";

const TOASTER_TIMEOUT = TimeSpan.fromSeconds(15);

export class HybridEmbeddedGameLoadingIndicatorModel extends GameLoadingIndicatorBaseModel implements IHybridEmbeddedGameLoadingIndicatorViewModel {
    constructor(services: IServiceFactoryExtended, private readonly gameRunner: GameRunnerService) {
        super(services);

        makeObservable(this, {
            downloadPercentage: observable.ref,
            currentPackage: observable.ref,
            totalPackages: observable.ref
        });

        this._sdkEventsSubscriptions.push(services.sdk.events.onHybridEmbeddedGameLoadingIndicatorUpdateV1(percentage => {
            this._updateProgress({
                percentage: percentage,
                numberOfPackages: '1/1'
            });
        }));

        this._sdkEventsSubscriptions.push(services.sdk.events.onHybridEmbeddedGameLoadingIndicatorUpdateV2(data => {
            this._updateProgress({
                percentage: data.statusBar,
                numberOfPackages: data.numberOfPackages
            });
        }));

        this._slowConnectionToasterTimer = setTimeout(this._showToaster, TOASTER_TIMEOUT.totalMilliseconds);

        this.services.analytics.sendAnalytics(ON_DEMAND.ON_DEMAND_CATEGORY, 'show_hybrid_loader');
    }

    private _sdkEventsSubscriptions: CGPEventSubscription[] = [];
    private _slowConnectionToasterTimer: any = null;

    private _showToaster = () => {
        this.services.sdk.showToaster({
            MessageID: 'hybrid_embedded_game_loading_indicator_toaster',
            Title: this.services.translation.translate('HYBRID_EMBEDDED_GAME_LOADING_SLOW_CONNECTION_TOASTER_TITLE'),
            SubTitle: this.services.translation.translate('HYBRID_EMBEDDED_GAME_LOADING_SLOW_CONNECTION_TOASTER_SUBTITLE'),
            Type: TOAST_TYPES.TIMED,
            DurationInSec: 10
        });
        this.services.analytics.sendAnalytics(ON_DEMAND.ON_DEMAND_CATEGORY, 'slow_internet_toaster', TOASTER_TIMEOUT.totalSeconds.toString(), this.downloadPercentage);
    }

    private _clearToasterTimer(): void {
        if(this._slowConnectionToasterTimer) {
            clearTimeout(this._slowConnectionToasterTimer);
            this._slowConnectionToasterTimer = null;
        }
    }

    private _updateProgress = (data: {percentage: number | string; numberOfPackages: string}) => {
        runInAction(() => {
            if(Check.isNumber(data.percentage)) {
                this.downloadPercentage = data.percentage
            } else {
                const percentage = parseInt(data.percentage);
                if(!Check.isNumber(percentage)) {
                    this.services.logger.error('Hybrid embedded game download percentage is not a number!');
                    this.downloadPercentage = 50; // just put something there
                } else {
                    this.downloadPercentage = percentage;
                }
            }

            const parts =  data.numberOfPackages.split('/');
            this.currentPackage = parseInt(parts[0]?.trim());
            this.totalPackages = parseInt(parts[1]?.trim());

            if(!Check.isNumber(this.currentPackage)) {
                this.currentPackage = 1;
            }

            if(!Check.isNumber(this.totalPackages)) {
                this.totalPackages = this.currentPackage;
            }

            //if download percentage is more than 10% then we don't show the slow connection toaster anymore
            if(this.downloadPercentage >= 10) {
                this._clearToasterTimer();
            }
        });
    }

    downloadPercentage: number = 0;

    currentPackage: number = 1;

    totalPackages: number = 1;


    dispose() {
        super.dispose();
        this._sdkEventsSubscriptions.forEach(s => s.off());
        this._sdkEventsSubscriptions = [];
        this._clearToasterTimer();
    }

    render(): React.ReactElement {
        return (
            <HybridEmbeddedGameLoadingIndicatorComponent viewModel={this}/>
        );
    }

    closeLoadingIndicator(): void {
        this.gameRunner.closeGameLoadingIndicator();
        this.services.sdk.eventsTrigger.hybridGameLoadingIndicatorClosed();

    }
}